<template>
  <Modal :model-value="true" @update:model-value="$emit('close')">
    <div class="flex flex-col gap-5">
      <div class="flex items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <h3 class="font-heading text-[20px]/[28px] font-bold text-stone-900">
            {{ t('slides_editor.components.picture_choice.title') }}
          </h3>
          <p class="text-sm/5 text-stone-600">
            <template v-if="brief">
              {{ t('slides_editor.components.picture_choice.looking_for') }}
              <span class="text-stone-900">{{ brief }}</span>
            </template>
            {{ t('slides_editor.components.picture_choice.note') }}
          </p>
        </div>
        <button
          type="button"
          @click="$emit('close')"
          :aria-label="t('slides_editor.components.picture_choice.close')"
          :title="t('slides_editor.components.picture_choice.close')"
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-stone-200 text-stone-600 hover:bg-stone-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4">
            <path stroke-linecap="round" d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Searching again from the editor is the design's next step, not this one. -->
      <div
        class="flex items-center gap-2.5 rounded-xl border border-stone-300 px-3 py-2 text-stone-500 opacity-60"
        :title="t('slides_editor.components.picture_choice.search_soon')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4">
          <circle cx="11" cy="11" r="8" />
          <path stroke-linecap="round" d="m21 21-4.3-4.3" />
        </svg>
        <span class="text-sm/5 text-stone-400">{{ t('slides_editor.components.picture_choice.search') }}</span>
      </div>

      <p v-if="loaded && candidates.length === 0" class="text-sm text-stone-500">
        {{ t('slides_editor.components.picture_choice.empty') }}
      </p>

      <div class="grid grid-cols-4 gap-3">
        <button
          v-for="candidate in candidates"
          :key="candidate.index"
          type="button"
          :disabled="saving"
          @click="picked = candidate"
          class="flex flex-col gap-1.5 rounded-xl border-2 p-1 text-left disabled:opacity-50"
          :class="candidate.index === chosen ? 'border-violet-600' : 'border-transparent hover:border-stone-300'"
        >
          <img :src="candidate.thumb" :alt="candidate.alt || ''" loading="lazy" class="h-24 w-full rounded-lg object-cover" />
          <span class="block truncate px-0.5 text-xs/4 text-stone-500">{{ candidate.credit }}</span>
        </button>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-4">
        <span class="text-xs/[18px] text-stone-500">
          <template v-if="picked">{{ t('slides_editor.components.picture_choice.chosen') }} {{ picked.credit }}</template>
        </span>
        <span class="flex items-center gap-3">
          <button
            type="button"
            @click="$emit('close')"
            class="rounded-xl border border-stone-300 px-4 py-2 text-sm/5 font-medium text-stone-900 hover:bg-stone-50"
          >
            {{ t('slides_editor.components.picture_choice.cancel') }}
          </button>
          <button
            type="button"
            :disabled="!picked || saving"
            @click="choose"
            class="rounded-xl border border-violet-600 bg-violet-600 px-4 py-2 text-sm/5 font-medium text-white disabled:opacity-50"
          >
            {{ t('slides_editor.components.picture_choice.use') }}
          </button>
        </span>
      </div>
    </div>
  </Modal>
</template>

<script>
import { computed, inject, onMounted, ref } from 'vue'
import { get, patch } from '@rails/request.js'
import { t } from '@/libs/i18n'
import Modal from '../shared/Modal.vue'

export default {
  name: 'PictureChoice',
  components: { Modal },
  props: {
    picturesPath: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const updateSlides = inject('updateSlides')
    const candidates = ref([])
    const brief = ref('')
    const loaded = ref(false)
    const saving = ref(false)
    const picked = ref(null)

    // What is on the slide already is what the picker opens on.
    const chosen = computed(() => (picked.value || candidates.value.find((one) => one.chosen) || {}).index)

    onMounted(async () => {
      const response = await get(props.picturesPath, { responseKind: 'json' })
      if (response.ok) {
        const body = await response.json
        candidates.value = body.candidates
        brief.value = body.brief
      }
      loaded.value = true
    })

    const choose = async () => {
      saving.value = true
      const response = await patch(props.picturesPath, {
        body: JSON.stringify({ candidate: picked.value.index }),
        headers: { 'Content-Type': 'application/json' },
        responseKind: 'json'
      })

      if (response.ok) {
        const { slide } = await response.json
        if (slide) updateSlides(slide)
        emit('close')
      }

      saving.value = false
    }

    return { candidates, brief, chosen, loaded, picked, saving, choose, t }
  }
}
</script>
